/* eslint-disable @typescript-eslint/no-explicit-any */
import { Canvas, Object as FabricObject, Group, ActiveSelection } from 'fabric';

import { useCanvasStore } from '../store';

import { TextEntity } from '../metaBullets/classes/TextEntity';

import { Marker } from '../metaBullets/classes/Marker';

import { isHaveTextObject } from './textObjects';

import { generateObjectId } from './generateId';

import { loadObjectsWithLayers } from './loadObjectsWithLayers';
import { isHaveUserImage } from './imageUtils';
import { isBullet, isBulletTextItem } from './groupUtils';

/**
 * Проверяет является ли объект TextEntity (часть метабулита)
 */
function isTextEntity(obj: any): obj is TextEntity {
  return TextEntity.isTextEntity(obj);
}

/**
 * Проверяет является ли объект Marker (маркер метабулита)
 */
function isMarker(obj: any): obj is Marker {
  return obj?.type === 'marker' && obj?.isMarker === true;
}

/**
 * Проверяет является ли объект контентом маркера
 */
function isMarkerContent(obj: any): boolean {
  return !!(obj as any).markerId && !isMarker(obj);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const copyObjects = async (canvas: Canvas, canvasId: string) => {
  const activeObjects = canvas.getActiveObjects();
  if (activeObjects && activeObjects.length > 0) {
    if (activeObjects.length === 1 && isBulletTextItem(activeObjects[0])) {
      return [];
    }

    console.debug('📋 [Copy] Filtering out MetaBullet objects:', {
      activeObjectsCount: activeObjects.length,
      activeObjectTypes: activeObjects.map((obj) => obj.type),
    });

    // 🚫 ИСКЛЮЧАЕМ ВСЕ ОБЪЕКТЫ СВЯЗАННЫЕ С МЕТАБУЛИТАМИ
    const filteredObjects = activeObjects.filter((obj) => {
      // Исключаем TextEntity (метабулит сущности)
      if (isTextEntity(obj)) {
        console.debug('🚫 [Copy] Excluding TextEntity from copy:', {
          id: obj.id,
          entityType: obj.entityType,
          metaBulletId: obj.metaBulletId,
        });
        return false;
      }

      // Исключаем Marker (маркерные коробки)
      if (isMarker(obj)) {
        console.debug('🚫 [Copy] Excluding Marker from copy:', {
          id: obj.id,
          entityId: obj.entityId,
        });
        return false;
      }

      // Исключаем контент маркеров (объекты с markerId)
      if (isMarkerContent(obj)) {
        console.debug('🚫 [Copy] Excluding Marker Content from copy:', {
          id: obj.id,
          markerId: (obj as any).markerId,
        });
        return false;
      }

      // Разрешаем копирование остальных объектов
      return true;
    });

    console.debug('📋 [Copy] After filtering:', {
      originalCount: activeObjects.length,
      filteredCount: filteredObjects.length,
      excludedCount: activeObjects.length - filteredObjects.length,
    });

    // Если после фильтрации ничего не осталось - возвращаем пустой массив
    if (filteredObjects.length === 0) {
      console.debug('🚫 [Copy] All objects were MetaBullet-related, nothing to copy');
      return [];
    }

    const copied = filteredObjects.map(async (obj) => {
      if (!obj.noCopy) {
        const clone = await obj.clone();
        // Восстанавливаем оригинальную прозрачность при копировании
        if (obj.originalOpacity !== undefined) {
          clone.opacity = obj.originalOpacity;
          delete clone.originalOpacity;
        }
        return clone;
      } else {
        return null;
      }
    });

    const settledPromises = await Promise.allSettled(copied);
    const clonedObjects = settledPromises
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value);

    clonedObjects.forEach((obj) => {
      if (obj) {
        obj.set('id', generateObjectId(canvasId));
        obj.set('inner_id', null);
        obj.set('textId', null);
      }
    });

    return clonedObjects;
  }
  return [];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const pasteObjects = async (
  canvas: Canvas,
  objectsToPaste: any[],
  targetCanvasId: string,
  options?: {
    insertCoordinates?: { left: number; top: number };
    offsetX?: number;
    offsetY?: number;
    isNeedSetActivePastedObject?: boolean;
  },
) => {
  if (objectsToPaste.length > 0) {
    const defaultOffsetX = options?.offsetX ?? 30;
    const defaultOffsetY = options?.offsetY ?? 30;
    const shouldSetActive = options?.isNeedSetActivePastedObject !== false; // true по умолчанию, если явно не указано false

    const resToPaste = await Promise.all(objectsToPaste.map((obj) => obj.clone()));
    const objectsToBackup: FabricObject[] = [];
    const pastedFabricObjects: FabricObject[] = []; // Для хранения ссылок на вставленные объекты

    resToPaste.forEach((obj, index) => {
      // Переменные для расчета позиции
      let left, top;

      // Получаем информацию о origin объекта
      const originX = obj.originX || 'left';
      const originY = obj.originY || 'top';

      // Получаем размеры объекта
      const objWidth = obj.getScaledWidth ? obj.getScaledWidth() : obj.width || 0;
      const objHeight = obj.getScaledHeight ? obj.getScaledHeight() : obj.height || 0;

      if (options?.insertCoordinates) {
        // Если заданы координаты вставки, учитываем origin объекта
        const { left: insertLeft, top: insertTop } = options.insertCoordinates;

        // Корректируем позицию в зависимости от origin
        if (originX === 'center') {
          left = insertLeft;
        } else if (originX === 'right') {
          left = insertLeft + objWidth / 2;
        } else {
          // left
          left = insertLeft - objWidth / 2;
        }

        if (originY === 'center') {
          top = insertTop;
        } else if (originY === 'bottom') {
          top = insertTop + objHeight / 2;
        } else {
          // top
          top = insertTop - objHeight / 2;
        }

        // Добавляем смещение для каждого последующего объекта
        left += index * defaultOffsetX;
        top += index * defaultOffsetY;
      } else {
        // Если координаты не заданы, используем существующие с добавлением смещения
        const baseOffsetX = (index + 1) * defaultOffsetX;
        const baseOffsetY = (index + 1) * defaultOffsetY;

        // Получаем текущие координаты
        const currentLeft = obj.left || 0;
        const currentTop = obj.top || 0;

        // Рассчитываем смещение с учетом origin и размеров объекта
        // Цель: объект должен быть визуально смещен на baseOffset от своей исходной позиции,
        // при этом всегда оставаясь в видимой области
        if (originX === 'center') {
          // Для объектов с центральной точкой привязки
          // Нужно убедиться, что левый край объекта не выходит за пределы видимой области
          left = currentLeft + baseOffsetX;

          // Проверяем, не находится ли левый край объекта слишком близко к краю канваса
          const leftEdge = left - objWidth / 2;
          if (leftEdge < baseOffsetX) {
            // Если левый край объекта оказывается ближе к краю канваса, чем baseOffsetX,
            // то сдвигаем объект вправо, чтобы обеспечить минимальный отступ
            left += baseOffsetX - leftEdge;
          }
        } else if (originX === 'right') {
          // Для объектов с правой точкой привязки
          // Нужно убедиться, что левый край объекта не выходит за пределы видимой области
          left = currentLeft + baseOffsetX;

          // Проверяем, не находится ли левый край объекта слишком близко к краю канваса
          const leftEdge = left - objWidth;
          if (leftEdge < baseOffsetX) {
            // Если левый край объекта оказывается ближе к краю канваса, чем baseOffsetX,
            // то сдвигаем объект вправо, чтобы обеспечить минимальный отступ
            left += baseOffsetX - leftEdge;
          }
        } else {
          // 'left'
          // Для объектов с левой точкой привязки
          // Просто добавляем смещение, так как left уже соответствует левому краю
          left = currentLeft + baseOffsetX;
        }

        // Аналогичная логика для вертикального смещения
        if (originY === 'center') {
          top = currentTop + baseOffsetY;

          const topEdge = top - objHeight / 2;
          if (topEdge < baseOffsetY) {
            top += baseOffsetY - topEdge;
          }
        } else if (originY === 'bottom') {
          top = currentTop + baseOffsetY;

          const topEdge = top - objHeight;
          if (topEdge < baseOffsetY) {
            top += baseOffsetY - topEdge;
          }
        } else {
          // 'top'
          top = currentTop + baseOffsetY;
        }
      }

      const newId = generateObjectId(targetCanvasId);
      obj.set({
        left,
        top,
        id: newId,
        // dirty: true,
        objectCaching: true, //false
      });
      if (obj.type.toLowerCase() === 'group' && obj instanceof Group) {
        if ('category' in obj && obj.category !== undefined && isBullet(obj)) {
          obj._objects.forEach((obj) => {
            obj.set('idGroup', newId);
            obj.set('id', generateObjectId(targetCanvasId));
          });
        }
      }

      objectsToBackup.push(obj.toJSON());
      pastedFabricObjects.push(obj); // Сохраняем ссылку на объект
    });

    //integration with backups
    if (objectsToBackup.length > 0) {
      const canvasStore = useCanvasStore.getState();
      const canvasState = canvasStore.getCanvasState(targetCanvasId);

      if (canvasState) {
        //remove future backups
        canvasState.backups = canvasState.backups.slice(0, canvasState.currentStateIndex + 1);

        const {
          objects: currentObjects,
          background: currentBackground,
          version: currentVersion,
        } = canvasState.backups[canvasState.currentStateIndex];

        const lastBackup = canvasState.backups[canvasState.backups.length - 1];
        const isChangedContent =
          isHaveTextObject(objectsToBackup) ||
          isHaveUserImage(objectsToBackup) ||
          (lastBackup && lastBackup.isChangedContent);

        //add new backup
        const newBackup = {
          background: currentBackground,
          version: currentVersion,
          objects: [...currentObjects, ...objectsToBackup],
          isChangedContent: isChangedContent,
        };
        canvasStore.addBackup(targetCanvasId, newBackup);

        //update current index
        canvasState.currentStateIndex = canvasState.backups.length - 1;

        //optional, if you need to save backup limit
        if (canvasState.backups.length > canvasState.backupDepth) {
          canvasState.backups.shift();
          canvasState.currentStateIndex--;
        }

        console.debug('Backup updated', canvasState.backups);

        // Загружаем объекты с помощью функции loadObjectsWithLayers
        if (canvasState.fabricInstance) {
          loadObjectsWithLayers(canvasState.fabricInstance, targetCanvasId, newBackup).then(() => {
            // После загрузки объектов, активируем их, если нужно
            if (shouldSetActive && pastedFabricObjects.length > 0) {
              // Находим объекты на канвасе по их ID
              const fabricInstance = canvasState.fabricInstance;
              if (fabricInstance) {
                const objectsToSelect = fabricInstance
                  .getObjects()
                  .filter((obj) =>
                    pastedFabricObjects.some((pastedObj) => pastedObj.id === obj.id),
                  );

                if (objectsToSelect.length > 0) {
                  // Если один объект, просто делаем его активным
                  if (objectsToSelect.length === 1) {
                    fabricInstance.setActiveObject(objectsToSelect[0]);
                  } else {
                    // Если несколько объектов, создаем ActiveSelection
                    const activeSelection = new ActiveSelection(objectsToSelect, {
                      canvas: fabricInstance,
                    });
                    fabricInstance.setActiveObject(activeSelection);
                  }
                  fabricInstance.requestRenderAll();
                }
              }
            }
          });
        }
      }
    }
  }
};

export const pasteObject = async (
  canvas: Canvas,
  objectToPaste: FabricObject,
  targetCanvasId: string,
  options?: {
    isNeedSetActivePastedObject?: boolean;
  },
) => {
  if (!objectToPaste) return;

  const setActive = options?.isNeedSetActivePastedObject !== false;

  const obj = await objectToPaste.clone();
  obj.set({
    id: generateObjectId(targetCanvasId),
    inner_id: null,
    textId: null,
    objectCaching: true,
  });

  const transform = canvas.viewportTransform;
  const scaleX = transform[0]; // horizontal scale (zoom)
  const scaleY = transform[3]; // vertical scale (zoom)
  const left = (canvas.getWidth() * scaleX) / 2 + obj.getScaledWidth() / 2;
  const top = (canvas.getHeight() * scaleY) / 2 + obj.getScaledHeight() / 2;
  console.log();
  console.log(left, top);
  obj.set({ left, top });
  canvas.add(obj);

  if (setActive) {
    canvas.setActiveObject(obj);
  }

  canvas.requestRenderAll();
  return obj;
};
